import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  CheckCheck,
  ExternalLink,
  Inbox,
  Loader2,
  Mail,
  MailCheck,
  MessageSquareText,
  MoreHorizontal,
  Paperclip,
  Phone,
  RefreshCcw,
  Search,
  Send,
  X,
} from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import {
  annoncerErreur, annoncerSucces, EtatErreur, EtatSansDonnees, SqueletteListe, validerFichier,
} from '../etats';

// Ce que le serveur accepte en pièce jointe d'une réponse (5 Mo).
const PIECE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];
const PIECE_TAILLE_MAX = 5 * 1024 * 1024;

/* ═══════════════════════════════════════════════════════════════════════════
   CENTRE DE MESSAGES

   La page empilait de grandes cartes, chacune répétant le message entier, les
   trois coordonnées de l'organisation, l'historique complet des réponses et
   trois boutons. Deux conséquences :

   - il fallait dérouler une page entière pour survoler cinq demandes, et rien
     ne permettait de comparer ce qui était traité de ce qui ne l'était pas ;
   - les mêmes actions, répétées à chaque carte, occupaient autant de place
     que le contenu — alors qu'on n'agit que sur une conversation à la fois.

   D'où le passage à deux panneaux, la disposition de toutes les messageries :
   à gauche on PARCOURT, à droite on LIT et on RÉPOND. La liste ne montre que
   ce qui sert à choisir — qui écrit, quand, l'amorce du message, où en est le
   dossier. Tout le reste attend d'avoir été demandé.

   Aucune logique métier ne change : mêmes appels, mêmes champs, mêmes
   permissions. C'est la même page, rangée autrement.
   ═══════════════════════════════════════════════════════════════════════════ */

const FILTRES = [
  { cle: 'toutes', libelle: 'Toutes' },
  { cle: 'non_lues', libelle: 'Non lues' },
  { cle: 'attente', libelle: 'En attente' },
  { cle: 'repondues', libelle: 'Répondues' },
  { cle: 'archivees', libelle: 'Archivées' },
];

const TRIS = [
  { cle: 'recent', libelle: 'Plus récent' },
  { cle: 'ancien', libelle: 'Plus ancien' },
];

/* Dans le fil, la bulle ne porte que l'HEURE : la date est déjà donnée
   une fois, par le séparateur de jour au-dessus. La répéter à chaque
   message la ferait lire vingt fois pour une seule information. */
const heureSeule = (valeur) => {
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

/* « Aujourd'hui » et « Hier » plutôt qu'une date : sur une conversation
   récente, c'est ce que la personne a en tête. Au-delà, la date
   complète, car « il y a 9 jours » demande un calcul. */
const jourLisible = (valeur) => {
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return '';
  const jour = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const n = new Date();
  const ecart = Math.round((jour - new Date(n.getFullYear(), n.getMonth(), n.getDate())) / 86400000);
  if (ecart === 0) return "Aujourd'hui";
  if (ecart === -1) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

/* Dans une liste, une date complète est du bruit : ce qu'on veut savoir, c'est
   « aujourd'hui, hier, ou avant ». L'heure ne compte que pour le jour même. */
const dateCourte = (valeur) => {
  if (!valeur) return '';
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return '';
  const maintenant = new Date();
  const memeJour = d.toDateString() === maintenant.toDateString();
  if (memeJour) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const hier = new Date(maintenant);
  hier.setDate(hier.getDate() - 1);
  if (d.toDateString() === hier.toDateString()) return 'Hier';
  const memeAnnee = d.getFullYear() === maintenant.getFullYear();
  return d.toLocaleDateString('fr-FR', memeAnnee
    ? { day: '2-digit', month: 'short' }
    : { day: '2-digit', month: '2-digit', year: '2-digit' });
};

/* ── Couleur d'organisation ────────────────────────────────────────────────
   Une pastille grise pour tout le monde, c'est une liste qu'on relit à chaque
   fois. Une teinte stable par organisation donne un repère qu'on reconnaît
   avant même d'avoir lu le nom — comme on reconnaît une conversation à sa
   photo.

   La teinte vient du NOM, pas d'un tirage : la même organisation garde la
   sienne d'une session à l'autre, et d'un poste à l'autre. Les six teintes
   restent dans la famille de la charte — verts, bleus profonds, ambre — pour
   ne pas transformer la colonne en nuancier. */
const TEINTES = [
  { fond: 'rgba(94,189,79,.16)',  texte: '#3F7F35' },
  { fond: 'rgba(27,26,101,.12)',  texte: '#2B2A78' },
  { fond: 'rgba(217,146,29,.16)', texte: '#8A5B0B' },
  { fond: 'rgba(30,120,140,.14)', texte: '#1C6470' },
  { fond: 'rgba(123,207,108,.20)', texte: '#3B7A30' },
  { fond: 'rgba(90,70,150,.13)',  texte: '#4A3A80' },
];

const teinteDe = (nom) => {
  const texte = nom || '?';
  let somme = 0;
  for (let i = 0; i < texte.length; i += 1) somme = (somme + texte.charCodeAt(i)) % 997;
  return TEINTES[somme % TEINTES.length];
};

const initiales = (nom) => (nom || '?')
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map((mot) => mot[0].toUpperCase()).join('') || '?';

function Avatar({ nom, taille = 38 }) {
  const t = teinteDe(nom);
  return (
    <span aria-hidden="true"
          className="inline-flex items-center justify-center rounded-controle shrink-0 font-semibold"
          style={{ width: taille, height: taille, background: t.fond, color: t.texte,
                   fontSize: taille * 0.36 }}>
      {initiales(nom)}
    </span>
  );
}

const sansAccents = (s) =>
  (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const etatDe = (m) => {
  if (!m.is_read) return 'nouveau';
  if (m.answered) return 'repondu';
  return 'attente';
};

/* ── Fiche organisation, en tiroir latéral ──────────────────────────────────
   Un panneau plutôt qu'une navigation : consulter le téléphone d'une
   organisation ne doit pas faire perdre la réponse en cours de rédaction. */
function TiroirOrganisation({ organisation, nombreDemandes, onFermer, onProfil }) {
  useEffect(() => {
    const auClavier = (e) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  const lignes = [
    { libelle: 'Type', valeur: organisation?.type },
    { libelle: 'Adresse e-mail', valeur: organisation?.email },
    { libelle: 'Téléphone', valeur: organisation?.contact },
    { libelle: 'Demandes reçues', valeur: String(nombreDemandes) },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true"
         aria-label="Fiche de l'organisation">
      <div className="absolute inset-0 bg-black/40" onClick={onFermer} aria-hidden="true" />
      <aside className="relative w-full max-w-sm h-full bg-[var(--bg-card)] border-l border-[var(--border)]
                        shadow-2xl p-6 overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0 flex items-start gap-3">
            <Avatar nom={organisation?.name} taille={44} />
            <div className="min-w-0">
            <p className="sous-titre-page">Organisation</p>
            <h2 className="titre-carte text-[var(--text-bold)] leading-snug mt-1 break-words">
              {organisation?.name || 'Organisation non renseignée'}
            </h2>
            </div>
          </div>
          <button type="button" onClick={onFermer} aria-label="Fermer"
                  style={{ minWidth: 44, minHeight: 44 }}
                  className="act act-tertiaire shrink-0">
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <dl className="space-y-4">
          {lignes.map((l) => (
            <div key={l.libelle}>
              <dt className="text-[12px] text-[var(--text-muted)]">{l.libelle}</dt>
              <dd className="text-[14px] text-[var(--text-bold)] mt-0.5 break-words">
                {l.valeur || <span className="text-[var(--text-muted)]">Non renseigné</span>}
              </dd>
            </div>
          ))}
        </dl>

        <button type="button" onClick={onProfil} disabled={!organisation?.id}
                style={{ minHeight: 44 }}
                className="act w-full mt-8">
          <ExternalLink className="w-4 h-4" aria-hidden="true" />
          Ouvrir le profil complet
        </button>
      </aside>
    </div>
  );
}

export default function Messages({ user }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  // Une actualisation en échec garde la liste : on le signale sans l'effacer.
  const [actualisationEchouee, setActualisationEchouee] = useState(false);
  const [toutLireEnCours, setToutLireEnCours] = useState(false);

  const [filtre, setFiltre] = useState('toutes');
  const [tri, setTri] = useState('recent');
  const [recherche, setRecherche] = useState('');
  const [selectionId, setSelectionId] = useState(null);
  const [tiroirOuvert, setTiroirOuvert] = useState(false);
  const [menuOuvert, setMenuOuvert] = useState(null);

  const [replyText, setReplyText] = useState('');
  const [piece, setPiece] = useState(null);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const filDeConversation = useRef(null);

  const loadMessages = useCallback(async ({ silent = false } = {}) => {
    if (!user?.id) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const data = await adminApi.getAdminNotifications(user.id, 50);
      setMessages(Array.isArray(data?.items) ? data.items : []);
      setUnreadCount(Number(data?.unread_count) || 0);
      setError(null);
      setActualisationEchouee(false);
      setCharge(true);
    } catch (err) {
      // L'objet entier : son statut dit lequel des cas afficher.
      setError(err);
      if (silent) setActualisationEchouee(true);
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadMessages();
    const intervalId = setInterval(() => loadMessages({ silent: true }), 25000);
    return () => clearInterval(intervalId);
  }, [loadMessages]);

  const stats = useMemo(() => {
    // Les chiffres portent sur la boite ACTIVE. Compter ce qu'on a range
    // rendrait « En attente » indechiffrable : on aurait traite dix demandes
    // sans voir le nombre bouger.
    const actifs = messages.filter((m) => !m.is_archived);
    const total = actifs.length;
    const nonLus = actifs.filter((m) => !m.is_read).length;
    const repondues = actifs.filter((m) => m.answered).length;
    return {
      total, nonLus, repondues,
      attente: Math.max(0, total - repondues),
      archivees: messages.length - total,
    };
  }, [messages]);

  /* Ce que montrerait chaque filtre. Les memes nombres que la rangee de
     compteurs supprimee -- mais lus la ou ils servent a decider, pas
     au-dessus dans un bandeau qu'on relit sans agir. */
  const compteurs = useMemo(() => ({
    toutes: stats.total,
    non_lues: stats.nonLus,
    attente: stats.attente,
    repondues: stats.repondues,
    archivees: stats.archivees,
  }), [stats]);

  const liste = useMemo(() => {
    const terme = sansAccents(recherche.trim());
    let resultat = messages.filter((m) => {
      // Une conversation rangee ne doit plus encombrer les autres vues :
      // c'est tout l'interet de la ranger. Elle reste consultable sous son
      // propre onglet.
      if (filtre === 'archivees') {
        if (!m.is_archived) return false;
      } else if (m.is_archived) {
        return false;
      }
      if (filtre === 'non_lues' && m.is_read) return false;
      if (filtre === 'repondues' && !m.answered) return false;
      if (filtre === 'attente' && m.answered) return false;
      if (!terme) return true;
      // La recherche porte sur ce qu'on a sous la main quand on cherche : le
      // nom, une adresse, un numero, ou une phrase dont on se souvient.
      const foin = [
        m.organisation?.name, m.organisation?.email, m.organisation?.contact,
        m.organisation?.type, m.comment,
        ...(Array.isArray(m.replies) ? m.replies.map((r) => r.message) : []),
      ].map(sansAccents).join(' ');
      return foin.includes(terme);
    });
    resultat = [...resultat].sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return tri === 'recent' ? db - da : da - db;
    });
    return resultat;
  }, [messages, filtre, recherche, tri]);

  const selection = useMemo(
    () => liste.find((m) => m.id === selectionId) || null,
    [liste, selectionId],
  );

  // Le fil se replace en bas a chaque changement de conversation : le dernier
  // echange est celui qui interesse, pas le premier.
  useEffect(() => {
    if (filDeConversation.current) {
      filDeConversation.current.scrollTop = filDeConversation.current.scrollHeight;
    }
  }, [selectionId, selection?.replies?.length]);

  const markAsRead = useCallback(async (messageId) => {
    if (!user?.id || !messageId) return;
    try {
      await adminApi.markAdminNotificationsRead({ adminId: user.id, notificationId: messageId });
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, is_read: true } : m)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Erreur marquage message lu:', err);
      annoncerErreur(err.message || 'Le message n’a pas pu être marqué comme lu.');
    }
  }, [user?.id]);

  const archiver = useCallback(async (message, valeur = true) => {
    if (!user?.id) return;
    try {
      await adminApi.archiverNotification({
        adminId: user.id, notificationId: message.id, archiver: valeur,
      });
      setMessages((prev) => prev.map((m) => (
        m.id === message.id ? { ...m, is_archived: valeur } : m
      )));
      // La conversation quitte la vue courante : garder sa sélection
      // afficherait un fil que la liste ne contient plus.
      setSelectionId((id) => (id === message.id ? null : id));
      setMenuOuvert(null);
      annoncerSucces(valeur ? 'Conversation archivée.' : 'Conversation remise dans la boîte.');
    } catch (err) {
      console.error('Erreur archivage :', err);
      annoncerErreur(err.message || (valeur ? 'La conversation n’a pas pu être archivée.' : 'La conversation n’a pas pu être restaurée.'));
    }
  }, [user?.id]);

  const markAllAsRead = async () => {
    if (!user?.id || unreadCount === 0) return;
    setToutLireEnCours(true);
    try {
      await adminApi.markAdminNotificationsRead({ adminId: user.id, markAll: true });
      setMessages((prev) => prev.map((m) => ({ ...m, is_read: true })));
      setUnreadCount(0);
      annoncerSucces('Tous les messages sont marqués comme lus.');
    } catch (err) {
      console.error('Erreur marquage global messages:', err);
      annoncerErreur(err.message || 'Les messages n’ont pas pu être marqués comme lus.');
    } finally {
      setToutLireEnCours(false);
    }
  };

  /* Ouvrir une conversation vaut lecture — c'est le geste qui marquait
     « lu » par un bouton séparé, que plus personne n'avait à cliquer. */
  const ouvrir = (message) => {
    setSelectionId(message.id);
    setReplyText('');
    setPiece(null);
    setFeedback(null);
    setMenuOuvert(null);
    if (!message.is_read) markAsRead(message.id);
  };

  const ouvrirProfil = () => {
    if (!selection?.organisation?.id) return;
    navigate(`/users/${selection.organisation.id}`);
  };

  const demandesDeLOrganisation = useMemo(() => {
    const id = selection?.organisation?.id;
    if (!id) return 0;
    return messages.filter((m) => m.organisation?.id === id).length;
  }, [messages, selection]);

  const sendReply = async () => {
    if (!selection) return;
    const cid = selection.contact_request_id;
    const text = replyText.trim();
    if (!cid) {
      setFeedback({ type: 'err', text: "Identifiant du message introuvable." });
      return;
    }
    if (!text) {
      setFeedback({ type: 'err', text: 'Veuillez saisir une réponse.' });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      const res = await adminApi.replyToContact(cid, text, piece);
      const nouvelle = res?.reply || {
        message: text,
        created_at: new Date().toISOString(),
        email_sent: !!res?.email_sent,
        sent_by: user?.first_name || 'Admin',
      };
      setMessages((prev) => prev.map((m) => (
        m.contact_request_id === cid
          ? { ...m, answered: true, is_read: true, replies: [...(m.replies || []), nouvelle] }
          : m
      )));
      setReplyText('');
      setPiece(null);
      // La réussite passe en toast ; l'e-mail non parti reste sous la saisie,
      // là où l'on décidera de renvoyer.
      if (res?.email_sent) {
        annoncerSucces(`Réponse envoyée par e-mail à ${res.recipient || "l'organisation"}.`);
      } else {
        setFeedback({ type: 'warn', text: "Réponse enregistrée, mais l'e-mail n'a pas pu être envoyé. Vérifiez la configuration e-mail." });
      }
    } catch (err) {
      setFeedback({ type: 'err', text: err.message || "Échec de l'envoi de la réponse." });
    } finally {
      setSending(false);
    }
  };

  const peutRepondre = !!selection?.contact_request_id && !!selection?.organisation?.email;

  return (
    <div className="animate-in flex-1 min-h-0 flex flex-col">
      {/* ── En-tête ────────────────────────────────────────────────────────
          Il occupait un quart de l'écran avant le premier message. Le titre
          reste, les chiffres passent en ligne. */}
      <header className="shrink-0 flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h1 className="titre-page">Messages</h1>
          <p className="text-[13px] text-[var(--text-muted)] mt-1.5">
            Demandes de contact reçues par la plateforme, et vos réponses.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Le tri a suivi la suppression de la rangée de compteurs : il
              vit ici, avec les autres actions d'écran, plutôt que seul
              sur une ligne qui ne portait que lui. */}
          <label className="sr-only" htmlFor="tri-messages">Trier les messages</label>
          <select id="tri-messages" value={tri} onChange={(e) => setTri(e.target.value)}
                  className="filtre shrink-0">
            {TRIS.map((o) => <option key={o.cle} value={o.cle}>{o.libelle}</option>)}
          </select>
          <button type="button" onClick={() => loadMessages({ silent: true })}
                  disabled={refreshing} aria-busy={refreshing || undefined}
                  className="act act-tertiaire">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Actualiser
          </button>
          <button type="button" onClick={markAllAsRead} disabled={unreadCount === 0 || toutLireEnCours}
                  aria-busy={toutLireEnCours || undefined}
                  style={{ minHeight: 44 }}
                  className="act">
            {toutLireEnCours
              ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              : <CheckCheck className="w-4 h-4" aria-hidden="true" />}
            Tout marquer lu
          </button>
        </div>
      </header>

      {actualisationEchouee && charge && (
        <div className="encart encart-attention mb-4 flex items-center justify-between gap-3 flex-wrap" role="status">
          <span>L’actualisation a échoué : ces messages sont peut-être anciens.</span>
          <button type="button" className="act act-sm act-contour" onClick={() => loadMessages({ silent: true })}>
            Réessayer
          </button>
        </div>
      )}

      {/* ── Boîte à deux panneaux ─────────────────────────────────────────── */}
      <div className="rounded-carte shadow-carte bg-[var(--bg-card)] overflow-hidden
                      flex flex-col lg:flex-row flex-1 min-h-0 lg:min-h-[460px]">

        {/* Colonne de gauche : on parcourt */}
        <div className={`lg:w-[360px] xl:w-[400px] shrink-0 flex flex-col min-h-0
                         lg:border-r border-[var(--border)]
                         ${selection ? 'hidden lg:flex' : 'flex'}`}>

          <div className="p-3 border-b border-[var(--border)] space-y-2.5 shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
                      aria-hidden="true" />
              <input
                type="search"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Organisation, e-mail, téléphone, contenu…"
                aria-label="Rechercher dans les messages"
                
                className="champ champ-loupe"
              />
            </div>

            {/* Les quatre filtres occupent toute la largeur de la colonne. Le
                tri a rejoint la barre du haut : partagé avec eux dans 360 px,
                il rognait « Répondues » et se lisait comme un cinquième
                onglet. */}
            {/* Le controle segmente du modele : la piste tient les options
                et l'active passe en encre pleine. `aria-pressed` suffit a
                la feuille pour savoir laquelle. */}
            <div className="segmente max-w-full overflow-x-auto" role="group"
                 aria-label="Filtrer les messages">
              {FILTRES.map((f) => {
                const n = compteurs[f.cle];
                return (
                  <button key={f.cle} type="button" onClick={() => setFiltre(f.cle)}
                          aria-pressed={filtre === f.cle}
                          className="whitespace-nowrap shrink-0">
                    {f.libelle}
                    {n > 0 && <span className="opacity-60 ml-1 tabular-nums">{n}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {!charge && loading && (
              <SqueletteListe lignes={6} className="!shadow-none !rounded-none" />
            )}

            {!charge && !loading && error && (
              <EtatErreur erreur={error} onReessayer={() => loadMessages()} compact />
            )}

            {charge && liste.length === 0 && (
              recherche.trim() ? (
                <EtatSansDonnees cas="sansResultat" compact titre={`Aucun message pour « ${recherche.trim()} »`}
                  action={<button type="button" className="act act-sm act-contour" onClick={() => setRecherche('')}>Effacer la recherche</button>} />
              ) : filtre !== 'toutes' ? (
                <EtatSansDonnees cas="sansFiltre" compact titre="Aucun message pour ce filtre"
                  aide="Les autres onglets en contiennent peut-être."
                  action={<button type="button" className="act act-sm act-contour" onClick={() => setFiltre('toutes')}>Réinitialiser</button>} />
              ) : (
                <EtatSansDonnees cas="vide" compact icone={Inbox} titre="Aucun message"
                  aide="Les demandes de contact envoyées depuis la plateforme arriveront ici." />
              )
            )}

            <ul>
              {charge && liste.map((m) => {
                const actif = m.id === selectionId;
                const etat = etatDe(m);
                return (
                  <li key={m.id} className="relative group">
                    {/* ── La ligne de conversation ───────────────────
                        DEUX lignes, comme toute messagerie : qui écrit et
                        quand ; puis l'amorce du message.

                        Elle en avait quatre — nom, aperçu, une rangée
                        d'étiquettes (« Répondu », « Fondation »), puis
                        « Répondu le 18/09/2026 11:32 ». Chacune répétait
                        ce que la conversation dit déjà à droite, et six
                        échanges ne tenaient plus dans la colonne.

                        Ce qui reste : l'état passe en une coche devant
                        l'aperçu, le non-lu en pastille comptée à droite.
                        Le type d'organisation et la date de réponse sont
                        dans le fil, qui est à un clic. */}
                    <button
                      type="button"
                      onClick={() => ouvrir(m)}
                      aria-current={actif ? 'true' : undefined}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-3
                                  transition-colors relative
                                  after:absolute after:left-[60px] after:right-0 after:bottom-0
                                  after:h-px after:bg-[var(--border)] last:after:hidden ${
                                    actif ? 'bg-[var(--vert-pale)] after:hidden' : 'hover:bg-[var(--bg-main)]'}`}
                    >
                      <Avatar nom={m.organisation?.name} taille={48} />

                      <span className="flex-1 min-w-0">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={`text-[15px] truncate ${
                            m.is_read ? 'font-medium text-[var(--text-bold)]'
                                      : 'font-semibold text-[var(--text-bold)]'}`}>
                            {m.organisation?.name || 'Organisation non renseignée'}
                          </span>
                          <span className={`text-[12px] tabular-nums shrink-0 ${
                            m.is_read ? 'text-[var(--text-muted)]' : 'text-[var(--vert-fonce)] font-semibold'}`}>
                            {dateCourte(m.created_at)}
                          </span>
                        </span>

                        <span className="flex items-center gap-1.5 mt-0.5">
                          {/* La coche dit « on a répondu » sans occuper de
                              ligne — la convention des messageries. */}
                          {etat === 'repondu' && (
                            <CheckCheck className="w-4 h-4 shrink-0 text-[var(--vert-fonce)]"
                                        strokeWidth={2} aria-label="Répondu" />
                          )}
                          <span className={`flex-1 min-w-0 truncate text-[13px] ${
                            m.is_read ? 'text-[var(--text-muted)]' : 'text-[var(--text-main)] font-medium'}`}>
                            {m.comment || 'Aucun message'}
                          </span>
                          {!m.is_read && (
                            <span className="shrink-0 min-w-[18px] h-[18px] px-1.5 rounded-controle
                                             bg-[var(--accent)] text-[#0B1620] text-[11px] font-bold
                                             grid place-items-center">
                              1
                            </span>
                          )}
                        </span>
                      </span>
                    </button>

                    {/* Actions secondaires : au survol, jamais en permanence. */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100
                                    focus-within:opacity-100 transition-opacity">
                      <button
                        type="button"
                        aria-label="Autres actions"
                        onClick={(e) => { e.stopPropagation(); setMenuOuvert(menuOuvert === m.id ? null : m.id); }}
                        className="act act-icone act-tertiaire">
                        <MoreHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                      {menuOuvert === m.id && (
                        <div className="absolute right-0 mt-1 w-48 rounded-pastille shadow-carte bg-[var(--bg-card)] shadow-xl overflow-hidden z-20">
                          {!m.is_read && (
                            <button type="button"
                                    onClick={(e) => { e.stopPropagation(); markAsRead(m.id); setMenuOuvert(null); }}
                                    className="w-full text-left px-3 py-2.5 text-[12.5px] text-[var(--text-main)]
                                               hover:bg-[var(--bg-main)]">
                              Marquer comme lu
                            </button>
                          )}
                          <button type="button"
                                  disabled={!m.organisation?.id}
                                  onClick={(e) => { e.stopPropagation(); setMenuOuvert(null); navigate(`/users/${m.organisation.id}`); }}
                                  className="w-full text-left px-3 py-2.5 text-[12.5px] text-[var(--text-main)]
                                             hover:bg-[var(--bg-main)] disabled:opacity-40">
                            Voir l'organisation
                          </button>
                          <button type="button"
                                  onClick={(e) => { e.stopPropagation(); archiver(m, !m.is_archived); }}
                                  className="w-full text-left px-3 py-2.5 text-[12.5px] text-[var(--text-main)]
                                             hover:bg-[var(--bg-main)]">
                            {m.is_archived ? "Sortir de l'archive" : 'Archiver'}
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Colonne de droite : on lit et on répond */}
        <div className={`flex-1 min-w-0 flex flex-col min-h-0 ${selection ? 'flex' : 'hidden lg:flex'}`}>
          {!selection && (
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 text-center">
              <MessageSquareText className="w-10 h-10 text-[var(--text-muted)]" aria-hidden="true" />
              <p className="mt-3 text-[13.5px] text-[var(--text-muted)] max-w-[36ch]">
                Choisissez une conversation à gauche pour la lire et y répondre.
              </p>
            </div>
          )}

          {selection && (
            <>
              <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-3 shrink-0">
                <button type="button" onClick={() => setSelectionId(null)}
                        aria-label="Retour à la liste"
                        style={{ minWidth: 44, minHeight: 44 }}
                        className="act act-tertiaire shrink-0">
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                </button>

                <Avatar nom={selection.organisation?.name} taille={40} />

                <div className="flex-1 min-w-0">
                  <h2 className="titre-carte text-[var(--text-bold)] truncate">
                    {selection.organisation?.name || 'Organisation non renseignée'}
                  </h2>
                  <p className="text-[12px] text-[var(--text-muted)] truncate">
                    {selection.organisation?.type || 'Type non renseigné'}
                  </p>
                </div>

                <button type="button" onClick={() => setTiroirOuvert(true)}
                        disabled={!selection.organisation?.id}
                        
                        className="act act-contour shrink-0">
                  <Building2 className="w-3.5 h-3.5" aria-hidden="true" />
                  <span className="hidden sm:inline">Voir organisation</span>
                </button>
              </div>

              {/* ── Le fil ───────────────────────────────────────────────
                  La forme des messageries de bureau : une toile teintée
                  qui n'est pas la carte, des bulles pleines sans filet,
                  l'heure DANS la bulle en bas à droite.

                  Le côté ET la surface distinguent les deux sens — jamais
                  la couleur seule : le fil se lit aussi bien sans
                  distinguer le vert du blanc. */}
              <div ref={filDeConversation}
                   className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-5 py-4 bg-[#EDF1EE]">

                <div className="flex justify-center mb-3">
                  <span className="px-3 h-6 inline-flex items-center rounded-controle bg-[var(--bg-card)]
                                   shadow-carte text-[11.5px] font-semibold text-[var(--text-muted)]">
                    {jourLisible(selection.created_at)}
                  </span>
                </div>

                {/* La demande entrante */}
                <div className="flex justify-start mt-2.5">
                  <div className="relative max-w-[85%] sm:max-w-[68%] px-3 py-2 shadow-carte
                                  bg-[var(--bg-card)] rounded-carte rounded-bl-md">
                    <p className="text-[12px] font-semibold text-[var(--vert-fonce)] m-0 mb-0.5">
                      {selection.organisation?.name || 'Organisation'}
                    </p>
                    <p className="text-[14px] leading-[1.45] text-[var(--text-bold)]
                                  whitespace-pre-line break-words m-0">
                      {selection.comment || (
                        <span className="text-[var(--text-muted)] italic">
                          Aucun message n&apos;accompagne cette demande.
                        </span>
                      )}
                    </p>
                    <span className="float-right ml-2 mt-1 text-[11px] text-[var(--text-muted)]">
                      {heureSeule(selection.created_at)}
                    </span>
                    <span className="clear-both block" />
                  </div>
                </div>

                {/* Les coordonnées : sous la première bulle, une fois. Ce
                    ne sont pas des messages, elles ne prennent donc pas la
                    forme d'une bulle. */}
                {(selection.organisation?.email || selection.organisation?.contact) && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 ml-1
                                  text-[11.5px] text-[var(--text-muted)]">
                    {selection.organisation?.email && (
                      <a href={`mailto:${selection.organisation.email}`}
                         className="inline-flex items-center gap-1.5 hover:text-[var(--text-bold)] break-all">
                        <Mail className="w-3 h-3 shrink-0" aria-hidden="true" />
                        {selection.organisation.email}
                      </a>
                    )}
                    {selection.organisation?.contact && (
                      <a href={`tel:${selection.organisation.contact}`}
                         className="inline-flex items-center gap-1.5 hover:text-[var(--text-bold)]">
                        <Phone className="w-3 h-3 shrink-0" aria-hidden="true" />
                        {selection.organisation.contact}
                      </a>
                    )}
                  </div>
                )}

                {(selection.replies || []).map((rep, idx) => {
                  const veille = idx === 0 ? selection.created_at : selection.replies[idx - 1].created_at;
                  const memeJour = new Date(veille).toDateString()
                                 === new Date(rep.created_at).toDateString();
                  return (
                    <div key={rep.id || idx}>
                      {!memeJour && (
                        <div className="flex justify-center my-3">
                          <span className="px-3 h-6 inline-flex items-center rounded-controle
                                           bg-[var(--bg-card)] shadow-carte text-[11.5px]
                                           font-semibold text-[var(--text-muted)]">
                            {jourLisible(rep.created_at)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-end mt-2.5">
                        <div className="relative max-w-[85%] sm:max-w-[68%] px-3 py-2 shadow-carte
                                        bg-[#DCF6D3] rounded-carte rounded-br-md">
                          <p className="text-[12px] font-semibold text-[var(--vert-fonce)] m-0 mb-0.5">
                            {rep.sent_by ? `Vous · ${rep.sent_by}` : 'Vous'}
                          </p>
                          <p className="text-[14px] leading-[1.45] text-[var(--text-bold)]
                                        whitespace-pre-line break-words m-0">
                            {rep.message}
                          </p>

                          {rep.piece_jointe && (
                            <a href={rep.piece_jointe} target="_blank" rel="noreferrer"
                               className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded-pastille
                                          bg-white/60 text-[12px] text-[var(--text-bold)] max-w-full">
                              <Paperclip className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                              <span className="truncate">{rep.piece_jointe_nom || 'Document joint'}</span>
                            </a>
                          )}

                          {/* L'accusé remplace la double coche : ici la
                              remise se fait par courriel, et « envoyée »
                              ne veut pas dire « lue ». On ne prétend donc
                              pas le savoir. */}
                          <span className="float-right flex items-center gap-1 ml-2 mt-1
                                           text-[11px] text-[var(--text-muted)]">
                            {heureSeule(rep.created_at)}
                            {rep.email_sent === false
                              ? <Mail className="w-3.5 h-3.5 text-[var(--attention)]"
                                      aria-label="Enregistrée, e-mail non envoyé" />
                              : <MailCheck className="w-3.5 h-3.5 text-[var(--vert-fonce)]"
                                           aria-label="Envoyée par e-mail" />}
                          </span>
                          <span className="clear-both block" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>


              {/* ── La saisie ────────────────────────────────────────────
                  Une seule barre : trombone, zone de texte, bouton rond.
                  La version précédente empilait un pavé de trois lignes
                  et une rangée d'actions en dessous — 140 px de hauteur
                  en permanence, pris sur le fil.

                  Entrée envoie, Maj+Entrée va à la ligne : la convention
                  de toutes les messageries. */}
              <div className="shrink-0 bg-[var(--bg-card)] px-3 sm:px-5 py-2.5">
                {feedback && (
                  <div role="status"
                       className={`encart mb-2 ${
                         feedback.type === 'ok' ? 'encart-ok'
                           : feedback.type === 'warn' ? 'encart-attention'
                           : 'encart-alerte'}`}>
                    {feedback.text}
                  </div>
                )}

                {piece && (
                  <span className="inline-flex items-center gap-1.5 min-w-0 mb-2 px-2.5 py-1.5
                                   rounded-pastille bg-[var(--bg-main)] text-[12px] text-[var(--text-bold)]">
                    <Paperclip className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate max-w-[16rem]">{piece.name}</span>
                    <button type="button" onClick={() => setPiece(null)}
                            aria-label="Retirer le document"
                            className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-bold)]">
                      <X className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  </span>
                )}

                <div className="flex items-end gap-2">
                  {/* Un DOCUMENT, pas une image : ce qu'on joint à une
                      réponse administrative — reçu, contrat, devis — se
                      lit et s'archive. Le serveur relit les premiers
                      octets du fichier, l'extension seule ne prouvant
                      rien. */}
                  <label title="Joindre un document"
                         className={`w-11 h-11 shrink-0 rounded-controle grid place-items-center
                                     text-[var(--text-muted)] hover:bg-[var(--bg-main)]
                                     hover:text-[var(--text-bold)] transition-colors
                                     ${peutRepondre ? 'cursor-pointer' : 'opacity-45 cursor-not-allowed'}`}>
                    <Paperclip className="w-[18px] h-[18px]" aria-hidden="true" />
                    <span className="sr-only">Joindre un document</span>
                    <input type="file" className="sr-only"
                           accept=".pdf,.doc,.docx,.xls,.xlsx"
                           disabled={!peutRepondre}
                           onChange={(e) => {
                             const fichier = e.target.files?.[0] || null;
                             e.target.value = '';
                             if (!fichier) return;
                             // Vérifié avant l'envoi : un refus après coup ferait
                             // retaper la réponse pour rien.
                             const refus = validerFichier(fichier, { extensions: PIECE_EXTENSIONS, tailleMax: PIECE_TAILLE_MAX });
                             if (refus) { setFeedback({ type: 'err', text: refus }); return; }
                             setFeedback(null);
                             setPiece(fichier);
                           }} />
                  </label>

                  <label htmlFor="reponse-message" className="sr-only">Votre réponse</label>
                  <textarea
                    id="reponse-message"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && replyText.trim() && peutRepondre) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    rows={1}
                    disabled={!peutRepondre}
                    placeholder={peutRepondre
                      ? `Répondre à ${selection.organisation.email}…`
                      : "Aucune adresse e-mail : impossible de répondre d'ici."}
                    className="flex-1 min-h-[44px] max-h-32 px-4 py-3 rounded-carte bg-[var(--bg-main)]
                               text-[14px] text-[var(--text-bold)] resize-none outline-none
                               focus:ring-2 focus:ring-[var(--accent)] disabled:opacity-60"
                  />

                  <button type="button" onClick={sendReply}
                          disabled={sending || !peutRepondre || !replyText.trim()}
                          aria-busy={sending || undefined}
                          aria-label="Envoyer la réponse"
                          className="w-11 h-11 shrink-0 rounded-controle grid place-items-center
                                     bg-[var(--accent)] text-[#0B1620] disabled:opacity-45
                                     hover:brightness-95 transition-[filter]">
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" />
                             : <Send className="w-[18px] h-[18px]" strokeWidth={2} />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {tiroirOuvert && selection && (
        <TiroirOrganisation
          organisation={selection.organisation}
          nombreDemandes={demandesDeLOrganisation}
          onFermer={() => setTiroirOuvert(false)}
          onProfil={() => { setTiroirOuvert(false); ouvrirProfil(); }}
        />
      )}
    </div>
  );
}
